import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  ArrowLeft,
  Download,
  RefreshCw,
  Users,
  LogIn,
  LogOut,
  Trash2,
  Plus,
  MapPin,
  Pencil,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { applyBrandColor } from "@/lib/theme";
import QuestionCard from "@/components/admin/QuestionCard";

const API = `/api`;

const DEFAULT_QUESTIONS = [
  { id: crypto.randomUUID(), type: "rating", title: "How was the food?", subtitle: "Rate from 1 (poor) to 5 (excellent)", required: true },
  { id: crypto.randomUUID(), type: "yesno", title: "Was the process of refundable payment to reserve your seat comfortable for you?", subtitle: "Please share your experience", required: true, comment: { enabled: true, required: false, trigger: "no", label: "If no why not?", placeholder: "Tell us why..." } },
  { id: crypto.randomUUID(), type: "rating", title: "Overall event feedback", subtitle: "Rate from 1 (poor) to 5 (excellent)", required: true },
  { id: crypto.randomUUID(), type: "choice", title: "Event frequency preference", subtitle: "In what frequency would you like the chapter to repeat event of similar types?", required: true, options: ["Quarterly", "Half yearly", "Yearly"] },
  { id: crypto.randomUUID(), type: "text", title: "Any suggestions?", subtitle: "Any further suggestions which could have made this event better? (Optional)", required: false, placeholder: "Share your thoughts..." },
];

const emptyQuestion = (type = "rating") => ({
  id: crypto.randomUUID(),
  type,
  title: "",
  subtitle: "",
  required: true,
  ...(type === "text" ? { placeholder: "" } : {}),
  ...(type === "choice" ? { options: ["", ""] } : {}),
  ...(type === "yesno" ? { comment: { enabled: false, required: false, trigger: "always", label: "", placeholder: "" } } : {}),
});

export default function Admin() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, checked_in: 0, checked_out: 0 });
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [eventType, setEventType] = useState(null);
  const [events, setEvents] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null); // null = create, object = edit
  const [eventForm, setEventForm] = useState({ name: "", color: "#8B1E2D", locationName: "", embedUrl: "", eventDate: "", feedbackQuestions: [] });
  const [publishing, setPublishing] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingAttendeeId, setDeletingAttendeeId] = useState(null);
  const [clearingEvent, setClearingEvent] = useState(false);

  const loadEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events`);
      setEvents(res.data);
      return res.data;
    } catch {
      return [];
    }
  }, []);

  // Fetch events on mount and initialize active event type
  useEffect(() => {
    loadEvents().then(data => {
      const active = data.find(e => e.is_active);
      setEventType(active?.name || data[0]?.name || "Learning Mahjong");
    });
  }, [loadEvents]);

  const fetchData = useCallback(async (silent = false, specificEvent = eventType) => {
    if (!specificEvent) return;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const [statsRes, attendeesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { params: { event_type: specificEvent } }),
        axios.get(`${API}/admin/attendees`, { params: { event_type: specificEvent } }),
      ]);
      setStats(statsRes.data);
      setAttendees(attendeesRes.data);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [eventType]);

  useEffect(() => {
    if (!eventType) return;
    fetchData();
    const interval = setInterval(() => fetchData(true), 15000);
    return () => clearInterval(interval);
  }, [fetchData, eventType]);

  const handleExportCSV = () => {
    window.open(`${API}/admin/export-csv?event_type=${encodeURIComponent(eventType)}`, "_blank");
  };

  const handleClearAll = async () => {
    if (!window.confirm("WARNING: This will permanently delete ALL attendee records from the database and the Google Sheet. This action cannot be undone. Are you sure?")) {
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/clear-all`);
      toast.success("All data has been cleared successfully");
      fetchData();
    } catch {
      toast.error("Failed to clear data");
    } finally {
      setLoading(false);
    }
  };

  const handleClearEvent = async () => {
    if (!eventType) return;
    if (!window.confirm(`Delete all ${attendees.length} attendee record(s) for "${eventType}"? This only affects this event and cannot be undone.`)) {
      return;
    }
    setClearingEvent(true);
    try {
      await axios.delete(`${API}/admin/attendees`, { params: { event_type: eventType } });
      toast.success(`Cleared attendees for ${eventType}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear event attendees");
    } finally {
      setClearingEvent(false);
    }
  };

  const handleDeleteAttendee = async (attendee) => {
    if (!window.confirm(`Delete ${attendee.name} (${attendee.event_id}) from this event? This cannot be undone.`)) {
      return;
    }
    setDeletingAttendeeId(attendee.id);
    try {
      await axios.delete(`${API}/admin/attendees/${attendee.id}`);
      toast.success(`${attendee.name} deleted`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete attendee");
    } finally {
      setDeletingAttendeeId(null);
    }
  };

  const openCreateModal = () => {
    setEditingEvent(null);
    setEventForm({ name: "", color: "#8B1E2D", locationName: "", embedUrl: "", eventDate: "", feedbackQuestions: DEFAULT_QUESTIONS });
    setShowEventModal(true);
  };

  const openEditModal = (e) => {
    setEditingEvent(e);
    setEventForm({
      name: e.name,
      color: e.color,
      locationName: e.location_name,
      embedUrl: e.location_embed_url,
      eventDate: e.event_date || "",
      feedbackQuestions: e.feedback_questions || [],
    });
    setShowEventModal(true);
  };

  const handleSaveEvent = async () => {
    if (!eventForm.name.trim() || !eventForm.color || !eventForm.locationName.trim()) {
      toast.error("Event name, color, and location name are required");
      return;
    }
    setPublishing(true);
    try {
      const payload = {
        name: eventForm.name.trim(),
        color: eventForm.color,
        location_name: eventForm.locationName.trim(),
        location_embed_url: eventForm.embedUrl.trim(),
        event_date: eventForm.eventDate || null,
        feedback_questions: eventForm.feedbackQuestions.length > 0 ? eventForm.feedbackQuestions : null,
      };
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, payload);
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/events`, payload);
        applyBrandColor(eventForm.color);
        toast.success("New event published");
      }
      const data = await loadEvents();
      const active = data.find(e => e.is_active);
      if (active) {
        setEventType(active.name);
        fetchData(false, active.name);
      }
      setShowEventModal(false);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : (editingEvent ? "Failed to update event" : "Failed to publish event"));
    } finally {
      setPublishing(false);
    }
  };

  const updateQuestion = (id, patch) => {
    setEventForm((f) => ({
      ...f,
      feedbackQuestions: f.feedbackQuestions.map((q) => (q.id === id ? { ...q, ...patch } : q)),
    }));
  };

  const changeQuestionType = (id, newType) => {
    setEventForm((f) => ({
      ...f,
      feedbackQuestions: f.feedbackQuestions.map((q) =>
        q.id === id ? { ...emptyQuestion(newType), id: crypto.randomUUID(), title: q.title, subtitle: q.subtitle, required: q.required } : q
      ),
    }));
  };

  const addQuestion = () => {
    setEventForm((f) => ({ ...f, feedbackQuestions: [...f.feedbackQuestions, emptyQuestion()] }));
  };

  const removeQuestion = (id) => {
    setEventForm((f) => ({ ...f, feedbackQuestions: f.feedbackQuestions.filter((q) => q.id !== id) }));
  };

  const moveQuestion = (id, direction) => {
    setEventForm((f) => {
      const idx = f.feedbackQuestions.findIndex((q) => q.id === id);
      const swapWith = idx + direction;
      if (swapWith < 0 || swapWith >= f.feedbackQuestions.length) return f;
      const next = [...f.feedbackQuestions];
      [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
      return { ...f, feedbackQuestions: next };
    });
  };

  const restoreDefaultQuestions = () => {
    setEventForm((f) => ({ ...f, feedbackQuestions: DEFAULT_QUESTIONS.map((q) => ({ ...q, id: crypto.randomUUID() })) }));
  };

  const addOption = (questionId) => {
    setEventForm((f) => ({
      ...f,
      feedbackQuestions: f.feedbackQuestions.map((q) =>
        q.id === questionId ? { ...q, options: [...(q.options || []), ""] } : q
      ),
    }));
  };

  const updateOption = (questionId, optIdx, value) => {
    setEventForm((f) => ({
      ...f,
      feedbackQuestions: f.feedbackQuestions.map((q) =>
        q.id === questionId
          ? { ...q, options: q.options.map((o, i) => (i === optIdx ? value : o)) }
          : q
      ),
    }));
  };

  const removeOption = (questionId, optIdx) => {
    setEventForm((f) => ({
      ...f,
      feedbackQuestions: f.feedbackQuestions.map((q) =>
        q.id === questionId ? { ...q, options: q.options.filter((_, i) => i !== optIdx) } : q
      ),
    }));
  };

  const handleDeleteEvent = async (id) => {
    if (!window.confirm("Delete this event? Attendee records will remain in the database.")) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/events/${id}`);
      toast.success("Event deleted");
      const data = await loadEvents();
      const active = data.find(e => e.is_active);
      if (active) {
        setEventType(active.name);
        fetchData(false, active.name);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete event");
    } finally {
      setDeletingId(null);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "short",
    });
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <div className="glass-header sticky top-0 z-50 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-sm text-[#525252] hover:text-[#0A0A0A] transition-colors"
              data-testid="admin-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <img
                src="/EO Odisha Logo.png"
                alt="EO Odisha Logo"
                className="h-8 w-auto object-contain"
              />
              <p className="text-[10px] text-[#525252] uppercase tracking-widest font-semibold mt-0.5">Admin Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="mr-2 w-48">
              <Select
                value={eventType || ""}
                onValueChange={(val) => {
                  setEventType(val);
                  fetchData(false, val);
                }}
              >
                <SelectTrigger className="h-9 rounded-none border-black/15 text-xs">
                  <SelectValue placeholder="Select Event" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {events.map(e => (
                    <SelectItem key={e.id} value={e.name}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="rounded-none border-black/15 text-xs uppercase tracking-wider"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={handleExportCSV}
              className="btn-brand rounded-none text-xs"
              data-testid="admin-csv-export"
            >
              <Download className="w-3 h-3" />
              Export CSV
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleClearEvent}
              disabled={loading || refreshing || clearingEvent || !eventType || attendees.length === 0}
              className="rounded-none text-xs uppercase tracking-wider hidden md:flex gap-2"
              data-testid="admin-clear-event"
            >
              <Trash2 className="w-3 h-3" />
              Clear Event
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleClearAll}
              disabled={loading || refreshing}
              className="rounded-none text-xs uppercase tracking-wider hidden md:flex gap-2"
              data-testid="admin-clear-all"
            >
              <Trash2 className="w-3 h-3" />
              Clear All
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-0 border border-black/10 mb-8 animate-fade-in">
          <div className="stat-card p-6 border-r border-black/10" data-testid="stat-total">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-[#525252]" />
              <p className="overline text-[#525252]">Total</p>
            </div>
            <p className="text-3xl sm:text-4xl font-black font-mono text-[#0A0A0A] tracking-tighter">
              {stats.total}
            </p>
          </div>
          <div className="stat-card p-6 border-r border-black/10" data-testid="stat-checked-in">
            <div className="flex items-center gap-2 mb-3">
              <LogIn className="w-4 h-4 text-[var(--brand)]" />
              <p className="overline text-[#525252]">Checked In</p>
            </div>
            <p className="text-3xl sm:text-4xl font-black font-mono text-[var(--brand)] tracking-tighter">
              {stats.checked_in}
            </p>
          </div>
          <div className="stat-card p-6" data-testid="stat-checked-out">
            <div className="flex items-center gap-2 mb-3">
              <LogOut className="w-4 h-4 text-[#525252]" />
              <p className="overline text-[#525252]">Checked Out</p>
            </div>
            <p className="text-3xl sm:text-4xl font-black font-mono text-[#0A0A0A] tracking-tighter">
              {stats.checked_out}
            </p>
          </div>
        </div>

        {/* Event Cards */}
        {events.length > 0 && (
          <div className="mb-8 animate-fade-in animate-delay-1">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[#0A0A0A] tracking-tight">Events</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={openCreateModal}
                className="rounded-none border-black/15 text-xs uppercase tracking-wider gap-1"
              >
                <Plus className="w-3 h-3" />
                Add New Event
              </Button>
            </div>
            <div className="flex border border-black/10 overflow-x-auto">
              {events.map((e, i) => (
                <div
                  key={e.id}
                  className={`flex-shrink-0 min-w-[180px] p-5 bg-white ${i < events.length - 1 ? "border-r border-black/10" : ""}`}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 flex-shrink-0" style={{ background: e.color }} />
                    <span className="text-xs font-bold text-[#0A0A0A] uppercase tracking-wide truncate">{e.name}</span>
                  </div>
                  <p className="text-3xl font-black font-mono text-[#0A0A0A] tracking-tighter leading-none mb-1">
                    {e.attendee_count}
                  </p>
                  <p className="overline text-[#525252] mb-4">Attendees</p>
                  {e.location_name && (
                    <p className="flex items-center gap-1 text-[10px] text-[#525252] mb-4 truncate">
                      <MapPin className="w-3 h-3 flex-shrink-0" />{e.location_name}
                    </p>
                  )}
                  {e.event_date && (
                    <p className="text-[10px] font-mono text-[#525252] mb-3">{e.event_date}</p>
                  )}
                  <div className="flex items-center gap-3">
                    {e.is_active ? (
                      <Badge className="rounded-none text-[10px] font-mono bg-[#0B6051] text-white border-[#0B6051]">
                        Active
                      </Badge>
                    ) : (
                      <button
                        onClick={() => handleDeleteEvent(e.id)}
                        disabled={deletingId === e.id}
                        className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#525252] hover:text-red-600 transition-colors disabled:opacity-40"
                      >
                        <Trash2 className="w-3 h-3" />
                        {deletingId === e.id ? "Deleting..." : "Delete"}
                      </button>
                    )}
                    <button
                      onClick={() => openEditModal(e)}
                      className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#525252] hover:text-[var(--brand)] transition-colors"
                    >
                      <Pencil className="w-3 h-3" />
                      Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Attendees Table */}
        <div className="animate-fade-in animate-delay-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-[#0A0A0A] tracking-tight">Attendees</h2>
            <p className="text-xs text-[#525252] font-mono">{attendees.length} records</p>
          </div>

          {loading ? (
            <div className="border border-black/10 bg-white p-12 text-center">
              <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-3 text-[#525252]" />
              <p className="text-sm text-[#525252]">Loading attendees...</p>
            </div>
          ) : attendees.length === 0 ? (
            <div className="border border-black/10 bg-white p-12 text-center" data-testid="empty-state">
              <Users className="w-8 h-8 mx-auto mb-3 text-[#525252]/40" />
              <p className="text-sm text-[#525252]">No attendees yet</p>
            </div>
          ) : (
            <div className="border border-black/10 bg-white overflow-hidden" data-testid="attendees-table">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#FAFAFA] border-b border-black/10">
                      <TableHead className="font-mono text-xs uppercase tracking-wider text-[#525252] font-semibold">Event ID</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-wider text-[#525252] font-semibold">Name</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-wider text-[#525252] font-semibold hidden sm:table-cell">Contact Number</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-wider text-[#525252] font-semibold">Membership Type</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-wider text-[#525252] font-semibold">Entry</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-wider text-[#525252] font-semibold">Exit</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-wider text-[#525252] font-semibold">Status</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-wider text-[#525252] font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {attendees.map((a) => (
                      <TableRow key={a.id} className="border-b border-black/5" data-testid={`attendee-row-${a.event_id}`}>
                        <TableCell className="font-mono text-sm font-semibold text-[#0A0A0A]">{a.event_id}</TableCell>
                        <TableCell className="text-sm font-medium text-[#0A0A0A]">{a.name}</TableCell>
                        <TableCell className="text-sm text-[#525252] hidden sm:table-cell">{a.contact}</TableCell>
                        <TableCell className="text-sm font-medium text-[#0A0A0A]">{a.type || "—"}</TableCell>
                        <TableCell className="text-xs font-mono text-[#525252]">{formatTime(a.entry_time)}</TableCell>
                        <TableCell className="text-xs font-mono text-[#525252]">{formatTime(a.exit_time)}</TableCell>
                        <TableCell>
                          {a.exit_time ? (
                            <Badge
                              variant="outline"
                              className="rounded-none text-xs font-mono border-black/20 text-[#525252]"
                              data-testid={`status-exited-${a.event_id}`}
                            >
                              Exited
                            </Badge>
                          ) : (
                            <Badge
                              className="rounded-none text-xs font-mono bg-[#0B6051] text-white border-[#0B6051]"
                              data-testid={`status-active-${a.event_id}`}
                            >
                              Active
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAttendee(a)}
                            disabled={deletingAttendeeId === a.id}
                            className="rounded-none text-[#525252] hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                            data-testid={`delete-attendee-${a.event_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Event Modal (create + edit) */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="rounded-none max-w-lg p-0 gap-0 border border-black/15">
          <DialogHeader className="px-6 py-5 border-b border-black/10">
            <DialogTitle className="font-black tracking-tight text-[#0A0A0A]">
              {editingEvent ? "Edit Event" : "Add New Event"}
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="details" className="w-full">
            <div className="px-6 pt-4">
              <TabsList className="w-full grid grid-cols-2">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="questions">Feedback Questions</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="details" className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Event Name */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-2 block">
                  Event Name
                </Label>
                <Input
                  placeholder="e.g. Learning Mahjong"
                  value={eventForm.name}
                  onChange={e => setEventForm(n => ({ ...n, name: e.target.value }))}
                  className="h-10 rounded-none border-black/20 bg-transparent focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-1 text-sm"
                />
              </div>

              {/* Brand Color */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-2 block">
                  Brand Color
                </Label>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={eventForm.color}
                    onChange={e => setEventForm(n => ({ ...n, color: e.target.value }))}
                    style={{ width: 40, height: 40, padding: 2, cursor: 'pointer', border: '1px solid rgba(0,0,0,0.2)', background: 'white', flexShrink: 0 }}
                  />
                  <Input
                    value={eventForm.color}
                    onChange={e => setEventForm(n => ({ ...n, color: e.target.value }))}
                    placeholder="#8B1E2D"
                    className="flex-1 h-10 rounded-none border-black/20 bg-transparent focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-1 text-sm font-mono"
                  />
                  <div
                    className="w-10 h-10 flex-shrink-0 border border-black/10"
                    style={{ background: eventForm.color }}
                  />
                </div>
              </div>

              {/* Event Date */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-2 block">
                  Event Date
                </Label>
                <Input
                  type="date"
                  value={eventForm.eventDate}
                  onChange={e => setEventForm(n => ({ ...n, eventDate: e.target.value }))}
                  className="h-10 rounded-none border-black/20 bg-transparent focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-1 text-sm font-mono"
                />
              </div>

              {/* Location Name */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-2 block">
                  Location Name
                </Label>
                <Input
                  placeholder="e.g. The Crown"
                  value={eventForm.locationName}
                  onChange={e => setEventForm(n => ({ ...n, locationName: e.target.value }))}
                  className="h-10 rounded-none border-black/20 bg-transparent focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-1 text-sm"
                />
              </div>

              {/* Google Maps Embed URL */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-2 block">
                  Google Maps Embed URL
                </Label>
                <Input
                  placeholder="https://www.google.com/maps/embed?pb=..."
                  value={eventForm.embedUrl}
                  onChange={e => setEventForm(n => ({ ...n, embedUrl: e.target.value }))}
                  className="h-10 rounded-none border-black/20 bg-transparent focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-1 text-sm font-mono"
                />
                <p className="text-[10px] text-[#525252] mt-1">
                  Google Maps → Share → Embed a map → copy the src URL
                </p>
              </div>

              {/* Location Preview */}
              {eventForm.embedUrl && (
                <div>
                  <p className="overline text-[#525252] mb-2">Location Preview</p>
                  <div className="border border-black/10 overflow-hidden">
                    <iframe
                      src={eventForm.embedUrl}
                      width="100%"
                      height="200"
                      style={{ border: 0, display: 'block' }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Location Preview"
                    />
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="questions" className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
              {editingEvent && editingEvent.attendee_count > 0 && (
                <div className="flex items-start gap-2 border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    {editingEvent.attendee_count} attendees already checked in under the current questions — edits won't affect their existing answers.
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between">
                <p className="text-xs text-[#525252]">
                  {eventForm.feedbackQuestions.length === 0
                    ? "No custom questions — this event uses the default 5-question survey."
                    : `${eventForm.feedbackQuestions.length} question${eventForm.feedbackQuestions.length === 1 ? "" : "s"} configured.`}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={restoreDefaultQuestions}
                  className="rounded-none border-black/15 text-xs uppercase tracking-wider"
                >
                  Restore Default Questions
                </Button>
              </div>

              <div className="space-y-4">
                {eventForm.feedbackQuestions.map((q, idx) => (
                  <QuestionCard
                    key={q.id}
                    question={q}
                    index={idx}
                    isFirst={idx === 0}
                    isLast={idx === eventForm.feedbackQuestions.length - 1}
                    onChangeType={changeQuestionType}
                    onUpdate={updateQuestion}
                    onMove={moveQuestion}
                    onRemove={removeQuestion}
                    onAddOption={addOption}
                    onUpdateOption={updateOption}
                    onRemoveOption={removeOption}
                  />
                ))}
              </div>

              <Button
                variant="outline"
                onClick={addQuestion}
                className="w-full rounded-none border-black/15 text-xs uppercase tracking-wider gap-1"
              >
                <Plus className="w-3 h-3" /> Add Question
              </Button>
            </TabsContent>
          </Tabs>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-black/10">
            <Button
              onClick={handleSaveEvent}
              disabled={publishing}
              className="w-full h-11 btn-brand rounded-none text-xs tracking-wider"
            >
              {publishing ? (
                <><RefreshCw className="w-3 h-3 animate-spin" /> {editingEvent ? "Saving..." : "Publishing..."}</>
              ) : (
                editingEvent ? "Save Changes" : "Publish Event"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
